/**
 * Parse the structured "why this action could not progress" payload that
 * the workflow engine surfaces through the standard APIResponse envelope.
 *
 * Both the generic engine endpoint and the claims execute endpoint route
 * blocking engine errors through ``_engine_error_response`` (see
 * ``backend/apps/workflows/api/views.py``), which packs a machine-readable
 * ``result`` alongside the human message:
 *
 *   { status, message, result: { code: "exception_required", binding } }
 *   { status, message, result: { code: "blocked_by_issue",   issues  } }
 *
 * ``exception_required`` is raised when the action carries an
 * ``ExceptionActionBinding`` and no valid grant was supplied - it is cleared
 * by creating a single-use ``ExceptionGrant`` and resubmitting with its uuid.
 * ``blocked_by_issue`` is raised when the instance has open ``WorkflowIssue``
 * rows - a grant does NOT clear it; each issue must be resolved first.
 */
import type { ExceptionActionBinding, WorkflowIssue } from './types'

export type EngineBlock =
  | {
      code: 'exception_required'
      binding: ExceptionActionBinding | null
      message: string
    }
  | {
      code: 'blocked_by_issue'
      issues: WorkflowIssue[]
      message: string
    }

/**
 * Extract the {@link EngineBlock} from an axios error thrown by an execute
 * call, or ``null`` when the error is not a structured engine block (a
 * plain validation error, a network failure, etc.).
 */
export function parseEngineBlockError(err: unknown): EngineBlock | null {
  if (!err || typeof err !== 'object') return null
  const response = (err as { response?: { data?: unknown } }).response
  const data = response?.data
  if (!data || typeof data !== 'object') return null

  const record = data as Record<string, unknown>
  const result = record.result
  if (!result || typeof result !== 'object') return null

  const detail = result as Record<string, unknown>
  const message = typeof record.message === 'string' ? record.message : ''

  if (detail.code === 'exception_required') {
    return {
      code: 'exception_required',
      binding: (detail.binding as ExceptionActionBinding | null) ?? null,
      message,
    }
  }
  if (detail.code === 'blocked_by_issue') {
    const issues = Array.isArray(detail.issues)
      ? (detail.issues as WorkflowIssue[])
      : []
    return { code: 'blocked_by_issue', issues, message }
  }
  return null
}
